import React from 'react';
import { FileText } from 'lucide-react';
import LegalPageLayout from '../components/legal/LegalPageLayout';
import LegalLink from '../components/legal/LegalLink';
import { legalBodyText, legalLinkClass, legalSectionTitle } from '../components/legal/legalStyles';

const TermsPage: React.FC = () => {
  return (
    <LegalPageLayout
      title="Terms of Service"
      lastUpdated="June 2026"
      icon={<FileText className="w-6 h-6 text-white" />}
      iconGradientClassName="from-blue-600 to-purple-600"
      cardTitle="Edyt Terms of Service Agreement"
      defaultBack={{ href: '/signup', label: 'Back to Sign Up' }}
      footer={{
        href: '/signup',
        label: 'Back to Sign Up',
        buttonGradientClassName:
          'from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700',
      }}
    >
      <div className={legalBodyText}>
        <section>
          <h2 className={legalSectionTitle}>1. Acceptance of Terms</h2>
          <p>
            By accessing and using Edyt (&quot;the Service&quot;), you accept and agree to be bound by the terms
            and provision of this agreement. If you do not agree to these terms, you should not use the Service.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>2. Description of Service</h2>
          <p>
            Edyt is an AI-powered customer success intelligence platform that helps teams analyze
            customer communications, discover insights, and manage customer relationships through
            integrations with communication tools like Slack.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>3. User Accounts</h2>
          <p>
            To use the Service, you must create an account by providing accurate and complete information.
            You are responsible for maintaining the confidentiality of your account credentials and for
            all activities that occur under your account.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>4. Data and Privacy</h2>
          <p>
            Your privacy is important to us. Our collection and use of personal information is governed
            by our Privacy Policy. By using the Service, you consent to the collection and use of
            information as outlined in our Privacy Policy.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>5. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Use the Service for any illegal or unauthorized purpose</li>
            <li>Violate any laws or regulations in your use of the Service</li>
            <li>Interfere with or disrupt the Service or servers connected to the Service</li>
            <li>Attempt to gain unauthorized access to any portion of the Service</li>
            <li>Use the Service to transmit any harmful or malicious code</li>
          </ul>
        </section>

        <section>
          <h2 className={legalSectionTitle}>6. Intellectual Property</h2>
          <p>
            The Service and its original content, features, and functionality are owned by Edyt and are
            protected by international copyright, trademark, patent, trade secret, and other intellectual
            property laws.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>7. Service Availability</h2>
          <p>
            We strive to provide reliable service, but we do not guarantee that the Service will be
            available at all times. The Service may be temporarily unavailable due to maintenance,
            updates, or technical issues.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>8. Data Processing (Enterprise)</h2>
          <p>
            Paid and enterprise customers are also governed by our{' '}
            <LegalLink to="/dpa">Data Processing Agreement</LegalLink>, which describes processor
            obligations, subprocessors, and security measures.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>9. Limitation of Liability</h2>
          <p>
            In no event shall Edyt be liable for any indirect, incidental, special, consequential,
            or punitive damages, including without limitation, loss of profits, data, use, goodwill,
            or other intangible losses.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>10. Termination</h2>
          <p>
            We may terminate or suspend your account and access to the Service immediately, without
            prior notice or liability, for any reason, including if you breach the Terms.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>11. Changes to Terms</h2>
          <p>
            We reserve the right to modify these terms at any time. We will notify users of any
            material changes by posting the new Terms of Service on this page and updating the
            &quot;Last updated&quot; date.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>12. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us at:{' '}
            <a href="mailto:legal@edyt.ai" className={legalLinkClass}>
              legal@edyt.ai
            </a>
          </p>
        </section>
      </div>
    </LegalPageLayout>
  );
};

export default TermsPage;
